import { unreachable } from '@chainflip/utils/assertion';
import {
  type BaseChainflipAsset,
  type ChainflipAsset,
  type AnyChainflipAsset,
  type AnyChainflipChain,
  type AnyChainMap,
  type ChainAssetMap,
  type InternalAssetMap,
  getAnyInternalAsset,
  type AssetSymbol,
  isAnyChainflipAsset,
} from '@chainflip/utils/chainflip';
import { assetOrder, chainOrder } from '@/shared/constants';
import { isTestnet } from '@/shared/featureFlags';
import { type AccountRole } from '@/shared/graphql/generated/explorer/graphql';
import { getChainflipNetwork } from '@/shared/utils/env';
import {
  arbiscanUrl,
  bscscanUrl,
  etherscanUrl,
  solscanUrl,
  tronscanUrl,
} from '@/shared/utils/helpers';

export const assetColors = {
  Eth: '#268AFF',
  Flip: '#FFFFFF',
  Usdc: '#70A4EC',
  Usdt: '#479079',
  Wbtc: '#E0BB72',
  Dot: '#FF33AF',
  Btc: '#FBCB9D',
  ArbUsdc: '#457DA7',
  ArbUsdt: '#329795',
  ArbEth: '#9CD2E6',
  Sol: '#B695EF',
  SolUsdc: '#4941BA',
  SolUsdt: '#2F6052',
  HubDot: '#D996BF',
  HubUsdt: '#798DA8',
  HubUsdc: '#708A82',
  Trx: '#B7282A',
  TrxUsdt: '#825B5B',
  // TODO: confirm with design
  Cbbtc: '#0052FF',
  Bnb: '#F0B90B',
  BscUsdt: '#8A7B3A',
} as const satisfies Record<AnyChainflipAsset, string>;

export const boostFeeTiers = [5, 10, 30];
export type BoostFeeTier = (typeof boostFeeTiers)[number];

export const accountRoleToPath = (role: AccountRole) => {
  switch (role) {
    case 'VALIDATOR':
      return 'validators';
    case 'OPERATOR':
      return 'operators';
    case 'BROKER':
      return 'brokers';
    case 'LIQUIDITY_PROVIDER':
      return 'lps';
    case 'TRADING_STRATEGY':
      return 'lps';
    case 'UNREGISTERED':
      return 'validators';
    default:
      return unreachable(role, 'unhandled account role');
  }
};

type ExplorerPath =
  | 'events'
  | 'swaps'
  | 'loans'
  | 'channels'
  | 'withdrawals'
  | 'validators'
  | 'blocks'
  | 'operators'
  | 'brokers'
  | 'lps'
  | 'delegators'
  | 'accounts'
  | 'authority-sets'
  | 'extrinsics';

export function buildChainflipExplorerLink(value: string, path?: ExplorerPath): string;
export function buildChainflipExplorerLink(
  value: string | undefined,
  path?: ExplorerPath,
): string | undefined;
export function buildChainflipExplorerLink(
  value: string | undefined,
  path: ExplorerPath = 'events',
) {
  if (!value) return undefined;

  return process.env.NEXT_PUBLIC_EXPLORER_URL
    ? new URL(`/${path}/${value}`, process.env.NEXT_PUBLIC_EXPLORER_URL).toString()
    : `/${path}/${value}`;
}
export const getChainflipExplorerTarget = () =>
  process.env.NEXT_PUBLIC_EXPLORER_URL ? '_blank' : undefined;

type NetworkAppPath = 'validators' | 'operators';

export function buildChainflipNetworkAppLink(value: string, path: NetworkAppPath): string;
export function buildChainflipNetworkAppLink(
  value: string | undefined,
  path: NetworkAppPath,
): string | undefined;
export function buildChainflipNetworkAppLink(value: string | undefined, path: NetworkAppPath) {
  if (!value) return undefined;

  return process.env.NEXT_PUBLIC_NETWORK_APP_URL
    ? new URL(`/network/${path}/${value}`, process.env.NEXT_PUBLIC_NETWORK_APP_URL).toString()
    : `/network/${path}/${value}`;
}

const buildSubscanUrl = (
  chain: 'Assethub' | 'Polkadot',
  type: 'address' | 'block' | 'tx',
  value: string | number,
) => {
  const subdomain = chain === 'Assethub' ? 'assethub-polkadot' : 'polkadot';
  if (type === 'block') return `https://${subdomain}.subscan.io/block/${value}`;
  if (type === 'address') {
    return `https://${subdomain}.subscan.io/account/${value}`;
  }
  if (type === 'tx') {
    return `https://${subdomain}.subscan.io/extrinsic/${value}`;
  }

  return unreachable(type, 'unhandled link type');
};

const internalNetworkLinks = {
  perseverance: {
    Assethub:
      'https://polkadot.js.org/apps/?rpc=wss://assethub.perseverance.chainflip.io#/explorer',
    Polkadot: 'https://polkadot.js.org/apps/?rpc=wss://rpc-pdot.chainflip.io#/explorer',
  },
  sisyphos: {
    Assethub: 'https://polkadot.js.org/apps/?rpc=wss://assethub.sisyphos.chainflip.io#/explorer',
    Polkadot: 'https://polkadot.js.org/apps/?rpc=wss://sisydot.chainflip.xyz/#/explorer',
  },
  backspin: {
    Assethub: '',
    Polkadot: '',
  },
  unknown: {
    Assethub: '',
    Polkadot: '',
  },
} as const;

export const buildExplorerLink = (
  chain: AnyChainflipChain | 'Chainflip',
  type: 'address' | 'block' | 'tx' | 'swap',
  value: string | number,
): string => {
  if (type === 'swap') {
    if (chain !== 'Chainflip') throw new Error('unsupported chain for swap link');
    if (!process.env.NEXT_PUBLIC_EXPLORER_URL) return `/swaps/${value}`;
    return new URL(`/swaps/${value}`, process.env.NEXT_PUBLIC_EXPLORER_URL).toString();
  }

  const chainflipNetwork = getChainflipNetwork();

  switch (chain) {
    case 'Bitcoin': {
      const baseUrl = `https://mempool.space${isTestnet ? '/testnet' : ''}`;
      return new URL(`/${type}/${value}`, baseUrl).toString();
    }
    case 'Assethub':
    case 'Polkadot':
      if (chainflipNetwork === 'mainnet') return buildSubscanUrl(chain, type, value);
      return internalNetworkLinks[chainflipNetwork][chain];
    case 'Ethereum':
      return etherscanUrl(type, value);
    case 'Arbitrum':
      return arbiscanUrl(type, value);
    case 'Solana':
      return solscanUrl(type, value);
    case 'Tron':
      return tronscanUrl(type, value);
    case 'Bsc':
      return bscscanUrl(type, value);
    case 'Chainflip': {
      let path = '';
      if (type === 'address') path = `/lps/${value}`;
      if (type === 'block' || type === 'tx') throw new Error('unsupported');
      if (!process.env.NEXT_PUBLIC_EXPLORER_URL) return path;
      return new URL(path, process.env.NEXT_PUBLIC_EXPLORER_URL).toString();
    }
    default:
      return unreachable(chain, 'unhandled chain');
  }
};

export const isChainflipAsset = (asset: string | undefined): asset is AnyChainflipAsset =>
  asset !== undefined && isAnyChainflipAsset(asset);

export const isBaseChainflipAsset = (asset: string): asset is BaseChainflipAsset =>
  isChainflipAsset(asset) && asset !== 'Usdc';

export const chainAssetMapToInternalAssetMap = <T, U>(
  chainAssetMap: ChainAssetMap<T>,
  cb: (id: AnyChainflipAsset, value: T) => U,
) =>
  Object.fromEntries(
    Object.entries(chainAssetMap).flatMap(([chain, assetMap]) =>
      Object.entries(assetMap).map(([asset, value]) => {
        const id = getAnyInternalAsset({
          chain: chain as AnyChainflipChain,
          asset: asset as AssetSymbol,
        });

        return [id, cb(id, value)];
      }),
    ),
  ) as InternalAssetMap<U>;

export const compareAssets = (a: AnyChainflipAsset, b: AnyChainflipAsset) =>
  assetOrder[a] - assetOrder[b];

export const compareChains = (a: AnyChainflipChain, b: AnyChainflipChain) =>
  chainOrder[a] - chainOrder[b];

export const chainflipChainLabels: AnyChainMap<{ name: string; shortName: string }> = {
  Bitcoin: { name: 'Bitcoin', shortName: 'Bitcoin' },
  Ethereum: { name: 'Ethereum', shortName: 'Ethereum' },
  Polkadot: { name: 'Polkadot', shortName: 'Polkadot' },
  Arbitrum: { name: 'Arbitrum', shortName: 'Arbitrum' },
  Solana: { name: 'Solana', shortName: 'Solana' },
  Assethub: { name: 'Polkadot', shortName: 'Polkadot' },
  Tron: { name: 'Tron', shortName: 'Tron' },
  Bsc: { name: 'BNB Chain', shortName: 'BSC' },
};

export const chainLabel = (chain: AnyChainflipChain) => chainflipChainLabels[chain].name;

export const chainShortLabel = (chain: AnyChainflipChain) => chainflipChainLabels[chain].shortName;

export type SupportedAsset = ChainflipAsset;
export type SupportedBaseAsset = BaseChainflipAsset;
